import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { isPro } from '../utils/featureGate';
import SEO from '../components/SEO';
import { useIsMobile } from '../hooks/useWindowWidth';
import EmailCapture from '../components/EmailCapture';

// ── PRO MARKET INTEL COMPONENTS ──────────────────────────────────────────────

function FearGreedGauge({ score, label }) {
  const color = score >= 75 ? '#22c55e' : score >= 55 ? '#86efac' : score >= 45 ? '#facc15' : score >= 25 ? '#f97316' : '#ef4444';
  const emoji = score >= 75 ? '😤' : score >= 55 ? '😊' : score >= 45 ? '😐' : score >= 25 ? '😟' : '😱';
  const pct = score + '%';
  return (
    <div style={pm.fgWrap}>
      <div style={pm.fgEmoji}>{emoji}</div>
      <div style={{ ...pm.fgScore, color }}>{score}</div>
      <div style={{ ...pm.fgLabel, color }}>{label}</div>
      <div style={pm.fgTrack}>
        <div style={{ ...pm.fgFill, width: pct, background: color }} />
      </div>
      <div style={pm.fgScale}>
        <span style={{ color: '#ef4444' }}>Extreme Fear</span>
        <span style={{ color: '#22c55e' }}>Extreme Greed</span>
      </div>
      <p style={pm.fgDesc}>Measures investor sentiment. Extreme fear can signal a buying opportunity. Extreme greed may mean the market is overheated.</p>
    </div>
  );
}

function SectorBar({ name, symbol, changePercent }) {
  const up = changePercent >= 0;
  const color = up ? '#22c55e' : '#ef4444';
  const barW = Math.min(Math.abs(changePercent) * 12, 100) + '%';
  return (
    <div style={pm.sectorRow}>
      <div style={pm.sectorName}>{name}</div>
      <div style={pm.sectorBar}>
        <div style={{ ...pm.sectorFill, width: barW, background: color, marginLeft: up ? '50%' : 'auto', marginRight: up ? 'auto' : '50%' }} />
      </div>
      <div style={{ ...pm.sectorPct, color }}>{up ? '+' : ''}{changePercent}%</div>
    </div>
  );
}

function ProMarketIntel({ pro }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  useEffect(() => {
    if (!pro) return; // Don't fetch for free users — just show teaser
    setLoading(true);
    fetch(`${API_URL}/api/pro-market`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); setLoaded(true); })
      .catch(() => setLoading(false));
  }, [pro]); // eslint-disable-line

  if (!pro) {
    // Teaser for free users — show what they're missing with blur
    return (
      <div style={pm.wrap}>
        <div style={pm.header}>
          <h2 style={pm.title}>📊 Pro Market Intel</h2>
          <span style={pm.proBadge}>🔒 Pro Only</span>
        </div>
        <p style={pm.subtitle}>Unlock deeper market data that tells you how investors are feeling, which sectors are moving, and where global money is flowing.</p>
        <div style={pm.teaserGrid}>
          {/* Fear & Greed teaser */}
          <div style={pm.teaserCard}>
            <div style={pm.teaserCardTitle}>😱 Fear & Greed Index</div>
            <div style={pm.blurBlock}>
              <div style={pm.blurFakeScore}>42</div>
              <div style={pm.blurFakeLabel}>Fear</div>
              <div style={pm.blurFakeBar}><div style={{ width: '42%', height: '100%', background: '#f97316', borderRadius: '4px' }} /></div>
            </div>
            <p style={pm.teaserDesc}>Is the market scared or greedy right now? This single number tells you the mood of the market — and can signal when to buy or hold.</p>
          </div>
          {/* Sectors teaser */}
          <div style={pm.teaserCard}>
            <div style={pm.teaserCardTitle}>🏭 Sector Performance</div>
            <div style={pm.blurBlock}>
              {[['Technology', '+1.2%', '#22c55e'], ['Energy', '-0.8%', '#ef4444'], ['Healthcare', '+0.3%', '#22c55e'], ['Financials', '-0.5%', '#ef4444']].map(([n, p, c]) => (
                <div key={n} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #1e293b', fontSize: '12px' }}>
                  <span style={{ color: '#64748b' }}>{n}</span><span style={{ color: c, fontWeight: 700 }}>{p}</span>
                </div>
              ))}
            </div>
            <p style={pm.teaserDesc}>See which sectors of the economy are winning and losing today — so you know where the money is actually moving.</p>
          </div>
          {/* BTC Dominance teaser */}
          <div style={pm.teaserCard}>
            <div style={pm.teaserCardTitle}>₿ BTC Dominance</div>
            <div style={pm.blurBlock}>
              <div style={{ fontSize: '36px', fontWeight: 900, color: '#f7931a', textAlign: 'center', padding: '12px 0' }}>57.2%</div>
              <div style={{ color: '#64748b', fontSize: '12px', textAlign: 'center' }}>of total crypto market cap</div>
            </div>
            <p style={pm.teaserDesc}>What percentage of the entire crypto market is Bitcoin? When dominance rises, altcoins often fall. When it drops, altcoin season may be near.</p>
          </div>
          {/* Currencies teaser */}
          <div style={pm.teaserCard}>
            <div style={pm.teaserCardTitle}>💱 Global Currencies</div>
            <div style={pm.blurBlock}>
              {[['EUR/USD', '1.0842', '#22c55e'], ['GBP/USD', '1.2731', '#ef4444'], ['USD/JPY', '149.8', '#22c55e']].map(([p, v, c]) => (
                <div key={p} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #1e293b', fontSize: '13px' }}>
                  <span style={{ color: '#94a3b8', fontWeight: 600 }}>{p}</span><span style={{ color: c, fontWeight: 700 }}>{v}</span>
                </div>
              ))}
            </div>
            <p style={pm.teaserDesc}>Track major currency pairs live. A strong or weak dollar affects everything from imports to your international investments.</p>
          </div>
          {/* VIX teaser */}
          <div style={pm.teaserCard}>
            <div style={pm.teaserCardTitle}>⚡ VIX — Volatility Index</div>
            <div style={pm.blurBlock}>
              <div style={{ fontSize: '36px', fontWeight: 900, color: '#f59e0b', textAlign: 'center', padding: '12px 0' }}>23.4</div>
              <div style={{ color: '#f59e0b', fontSize: '13px', textAlign: 'center', fontWeight: 700 }}>Moderate volatility</div>
            </div>
            <p style={pm.teaserDesc}>The VIX measures how nervous investors are right now. Below 20 = calm, above 30 = fear. It spikes before big drops — smart investors watch it closely.</p>
          </div>
        </div>
        <div style={pm.upgradeBar}>
          <div>
            <div style={pm.upgradeTitle}>Unlock Pro Market Intel</div>
            <div style={pm.upgradeSub}>VIX · Fear &amp; Greed · BTC Dominance · Sector Performance · Extended Crypto · Global Currencies</div>
          </div>
          <Link to="/signup?plan=pro" style={pm.upgradeBtn}>Get Pro — $12/mo →</Link>
        </div>
      </div>
    );
  }

  if (loading || !loaded) return (
    <div style={pm.wrap}>
      <h2 style={pm.title}>📊 Pro Market Intel</h2>
      <div style={{ color: '#64748b', padding: '24px 0' }}>Loading pro data...</div>
    </div>
  );

  if (!data) return null;

  return (
    <div style={pm.wrap}>
      <div style={pm.header}>
        <h2 style={pm.title}>📊 Pro Market Intel</h2>
        <span style={pm.proActiveBadge}>✅ Pro</span>
      </div>
      <div style={pm.proGrid}>
        {/* Fear & Greed */}
        {data.fearGreed && (
          <div style={pm.proCard}>
            <div style={pm.proCardTitle}>😱 Fear &amp; Greed Index</div>
            <FearGreedGauge score={data.fearGreed.score} label={data.fearGreed.label} />
          </div>
        )}
        {/* VIX */}
        {data.vix && (
          <div style={pm.proCard}>
            <div style={pm.proCardTitle}>⚡ VIX — Market Volatility</div>
            <div style={{ ...pm.btcDomNum, color: data.vix.score < 20 ? '#22c55e' : data.vix.score < 30 ? '#f59e0b' : '#ef4444' }}>
              {data.vix.score}
            </div>
            <p style={pm.btcDomDesc}>
              {data.vix.score < 20 ? 'Low volatility — markets are calm' : data.vix.score < 30 ? 'Moderate volatility — caution advised' : 'High volatility — fear is elevated'}
            </p>
            {data.vix.changePercent != null && (
              <div style={{ textAlign: 'center', fontSize: '13px', fontWeight: 700, color: data.vix.changePercent >= 0 ? '#ef4444' : '#22c55e', marginTop: '4px' }}>
                {data.vix.changePercent >= 0 ? '+' : ''}{data.vix.changePercent}% today
              </div>
            )}
            <p style={pm.fgDesc}>VIX above 30 = investors are nervous. Below 20 = calm markets. Spikes often signal a sell-off is happening.</p>
          </div>
        )}
        {/* BTC Dominance */}
        {data.btcDominance && (
          <div style={pm.proCard}>
            <div style={pm.proCardTitle}>₿ BTC Dominance</div>
            <div style={pm.btcDomNum}>{data.btcDominance}%</div>
            <p style={pm.btcDomDesc}>of total crypto market cap is Bitcoin</p>
            {data.totalMarketCap && <div style={pm.btcDomTotal}>Total crypto market: ${data.totalMarketCap}T</div>}
            <p style={pm.fgDesc}>High BTC dominance = investors prefer Bitcoin over altcoins. When it drops, money is flowing into altcoins (altcoin season).</p>
          </div>
        )}
        {/* Extended Crypto — Pro only (BTC/ETH already in free tier) */}
        {data.crypto?.length > 0 && (
          <div style={pm.proCard}>
            <div style={pm.proCardTitle}>🪙 Extended Crypto</div>
            {data.crypto.map((c, i) => (
              <div key={i} style={pm.currencyRow}>
                <div>
                  <div style={pm.currencyPair}>{c.name}</div>
                  <div style={pm.currencyBase}>{c.symbol}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={pm.currencyPrice}>${c.price?.toLocaleString()}</div>
                  {c.changePercent != null && (
                    <div style={{ color: c.changePercent >= 0 ? '#22c55e' : '#ef4444', fontSize: '12px', fontWeight: 700 }}>
                      {c.changePercent >= 0 ? '+' : ''}{c.changePercent}%
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Currencies */}
        {data.currencies?.length > 0 && (
          <div style={pm.proCard}>
            <div style={pm.proCardTitle}>💱 Global Currencies</div>
            {data.currencies.map((c, i) => (
              <div key={i} style={pm.currencyRow}>
                <div>
                  <div style={pm.currencyPair}>{c.pair}</div>
                  <div style={pm.currencyBase}>{c.base}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={pm.currencyPrice}>{c.price}</div>
                  {c.changePercent != null && (
                    <div style={{ color: c.changePercent >= 0 ? '#22c55e' : '#ef4444', fontSize: '12px', fontWeight: 700 }}>
                      {c.changePercent >= 0 ? '+' : ''}{c.changePercent}%
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Sectors */}
        {data.sectors?.length > 0 && (
          <div style={{ ...pm.proCard, gridColumn: '1 / -1' }}>
            <div style={pm.proCardTitle}>🏭 Sector Performance (Today)</div>
            <div style={pm.sectorGrid}>
              {[...data.sectors].sort((a, b) => b.changePercent - a.changePercent).map((s, i) => (
                <SectorBar key={i} {...s} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const pm = {
  wrap: { margin: '40px 0', padding: 'clamp(16px, 4vw, 32px)', background: '#0d1829', borderRadius: '16px', border: '1px solid #1e293b', overflow: 'hidden' },
  header: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' },
  title: { fontSize: '22px', fontWeight: '800', color: '#f1f5f9', margin: 0 },
  subtitle: { color: '#64748b', fontSize: '14px', marginBottom: '24px', lineHeight: 1.6 },
  proBadge: { background: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', padding: '3px 10px', fontSize: '12px', fontWeight: '700' },
  proActiveBadge: { background: 'rgba(34,197,94,0.1)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '6px', padding: '3px 10px', fontSize: '12px', fontWeight: '700' },
  // Teaser
  teaserGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '24px' },
  teaserCard: { background: '#0a1628', border: '1px solid #1e293b', borderRadius: '12px', padding: '20px' },
  teaserCardTitle: { fontSize: '14px', fontWeight: '700', color: '#94a3b8', marginBottom: '12px', letterSpacing: '0.3px' },
  teaserDesc: { color: '#475569', fontSize: '12px', lineHeight: 1.6, marginTop: '12px', marginBottom: 0 },
  blurBlock: { filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none', background: '#111827', borderRadius: '8px', padding: '12px' },
  blurFakeScore: { fontSize: '48px', fontWeight: 900, color: '#f97316', textAlign: 'center' },
  blurFakeLabel: { color: '#f97316', textAlign: 'center', fontWeight: 700, marginBottom: '10px' },
  blurFakeBar: { height: '8px', background: '#1e293b', borderRadius: '4px', overflow: 'hidden' },
  upgradeBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg, rgba(14,165,233,0.08), rgba(99,102,241,0.08))', border: '1px solid rgba(14,165,233,0.2)', borderRadius: '12px', padding: '20px 24px', gap: '16px', flexWrap: 'wrap' },
  upgradeTitle: { color: '#f1f5f9', fontWeight: '700', fontSize: '15px', marginBottom: '4px' },
  upgradeSub: { color: '#64748b', fontSize: '13px' },
  upgradeBtn: { background: 'linear-gradient(135deg, #0ea5e9, #6366f1)', color: '#fff', borderRadius: '8px', padding: '10px 20px', fontWeight: '700', fontSize: '14px', textDecoration: 'none', whiteSpace: 'nowrap' },
  // Pro live data
  proGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: '16px' },
  proCard: { background: '#0a1628', border: '1px solid #1e293b', borderRadius: '12px', padding: '20px', minWidth: 0 },
  proCardTitle: { fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  // Fear & Greed
  fgWrap: { textAlign: 'center' },
  fgEmoji: { fontSize: '40px', marginBottom: '8px' },
  fgScore: { fontSize: '52px', fontWeight: 900, lineHeight: 1 },
  fgLabel: { fontSize: '16px', fontWeight: '700', marginBottom: '12px', marginTop: '4px' },
  fgTrack: { height: '10px', background: '#1e293b', borderRadius: '5px', overflow: 'hidden', marginBottom: '6px' },
  fgFill: { height: '100%', borderRadius: '5px', transition: 'width 0.8s ease' },
  fgScale: { display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '12px' },
  fgDesc: { color: '#475569', fontSize: '12px', lineHeight: 1.6, marginTop: '10px', textAlign: 'left' },
  // BTC Dom
  btcDomNum: { fontSize: '52px', fontWeight: 900, color: '#f7931a', textAlign: 'center', lineHeight: 1 },
  btcDomDesc: { color: '#64748b', fontSize: '13px', textAlign: 'center', marginTop: '6px' },
  btcDomTotal: { color: '#475569', fontSize: '12px', textAlign: 'center', marginTop: '4px', marginBottom: '8px' },
  // Currencies
  currencyRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #1e293b' },
  currencyPair: { color: '#f1f5f9', fontWeight: '700', fontSize: '14px' },
  currencyBase: { color: '#475569', fontSize: '11px', marginTop: '2px' },
  currencyPrice: { color: '#e2e8f0', fontWeight: '700', fontSize: '15px' },
  // Sectors
  sectorGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: '8px' },
  sectorRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  sectorName: { color: '#94a3b8', fontSize: '12px', width: '140px', flexShrink: 0 },
  sectorBar: { flex: 1, height: '8px', background: '#1e293b', borderRadius: '4px', overflow: 'hidden', display: 'flex' },
  sectorFill: { height: '100%', borderRadius: '4px', transition: 'width 0.6s ease' },
  sectorPct: { fontSize: '12px', fontWeight: '700', width: '52px', textAlign: 'right', flexShrink: 0 },
};

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// ── Pre-written plain English explainers ───────────────────────────────────
const EXPLAINERS = {
  BTC: {
    what: 'Bitcoin is the world\'s first cryptocurrency — a digital currency that exists entirely online, with no central bank or government controlling it. There will only ever be 21 million Bitcoin in existence.',
    why: 'Some people invest because of its fixed supply (like digital gold), its growing acceptance by major institutions, and as a potential hedge against currency inflation.',
    risks: 'Bitcoin is extremely volatile — it has dropped 50–80% in value multiple times. It has no physical backing, faces ongoing regulatory uncertainty, and can be lost if you lose access to your wallet.',
  },
  ETH: {
    what: 'Ethereum is the second-largest cryptocurrency. Beyond being digital money, it\'s a programmable platform that powers decentralized apps, "smart contracts," and most of the crypto ecosystem.',
    why: 'It powers the largest ecosystem of crypto applications. Many see it as "digital infrastructure" — like investing in the internet itself in the early days.',
    risks: 'Highly volatile like all crypto. Competes with dozens of rival blockchains. Technology is complex and constantly evolving, which introduces upgrade risk.',
  },
  SOL: {
    what: 'Solana is a cryptocurrency and fast programmable blockchain, often called an Ethereum competitor. It processes transactions significantly faster and cheaper than older blockchains.',
    why: 'Some investors are drawn to its speed, lower fees, and growing developer activity building apps on its network.',
    risks: 'Much newer and smaller than Bitcoin or Ethereum. Has experienced several major outages. High volatility and uncertain long-term adoption.',
  },
  'S&P 500': {
    what: 'The S&P 500 tracks the 500 largest publicly traded US companies — Apple, Microsoft, Amazon, etc. It\'s the most widely followed benchmark of the overall US stock market.',
    why: 'Historically has returned around 10% per year on average over long periods. Buying an S&P 500 index fund gives you instant exposure to 500 companies with one purchase.',
    risks: 'Can drop 30–50% during recessions (it fell ~50% in 2008–2009 and ~34% in the 2020 COVID crash). Returns are not guaranteed and past performance doesn\'t predict future results.',
  },
  DOW: {
    what: 'The Dow Jones Industrial Average tracks just 30 large, well-established US companies — often called "blue chips" — like Apple, JPMorgan, Boeing, and Nike.',
    why: 'It\'s been around since 1896 and is considered a barometer of the US economy. Blue chip companies often pay dividends and tend to be more stable than smaller companies.',
    risks: 'Only 30 companies means far less diversification than the S&P 500. A single company\'s bad news can move the whole index significantly.',
  },
  NASDAQ: {
    what: 'The Nasdaq Composite tracks over 3,000 companies, but is heavily weighted toward technology — Apple, Microsoft, Google, Amazon, Meta, and Nvidia make up a large chunk of it.',
    why: 'Tech companies have driven significant growth over the past 20 years. Higher growth potential than the broader market.',
    risks: 'More volatile than the S&P 500. A tech sector downturn hits the Nasdaq much harder. The Nasdaq fell over 75% during the 2000–2002 dot-com crash.',
  },
  RUT: {
    what: 'The Russell 2000 tracks 2,000 small US companies. It\'s the most-watched benchmark for small-cap stocks — businesses with smaller market values than the S&P 500.',
    why: 'Small companies can grow faster than large ones. Some investors add small-cap exposure for diversification and higher growth potential.',
    risks: 'Small companies are riskier — they have less cash, less brand recognition, and are more sensitive to economic downturns. More volatile than large-cap indices.',
  },
  VIX: {
    what: 'The VIX measures how much volatility (price swings) investors expect in the stock market over the next 30 days. Often called the "Fear Index." High VIX = high fear. Low VIX = calm market.',
    why: 'The VIX itself is not something most people invest in directly. It\'s a sentiment indicator — a high VIX can signal a buying opportunity; a low VIX can signal complacency.',
    risks: 'This is a market indicator, not an investable asset for most everyday investors. VIX-based products exist but are complex and not suitable for beginners.',
  },
  GOLD: {
    what: 'Gold is a precious metal that humans have used as a store of value for thousands of years. Today you can own it physically (bars, coins) or through ETFs and stocks.',
    why: 'Often called a "safe haven" — it tends to hold value or rise during economic crises, stock market crashes, and periods of high inflation. It\'s been a store of value for 5,000 years.',
    risks: 'Gold generates no income (no dividends or interest). Its price can be volatile. It can underperform stocks during long bull markets. Storage and insurance costs apply for physical gold.',
  },
  OIL: {
    what: 'Crude oil is the world\'s most traded commodity. It powers vehicles, is used in plastics, and drives huge portions of the global economy. The price affects gasoline, shipping, and manufacturing costs.',
    why: 'Some investors use oil as a hedge against inflation or geopolitical instability. When supply is disrupted, oil prices can spike significantly.',
    risks: 'Very volatile — heavily influenced by OPEC decisions, geopolitics, and economic cycles. Long-term demand is uncertain as electric vehicles and renewable energy grow.',
  },
  SILVER: {
    what: 'Silver is a precious metal used both as a store of value (like gold) and in industrial applications — solar panels, electronics, medical equipment, and more.',
    why: 'Dual demand from investors and industry. Historically cheaper than gold, making it more accessible. Can outperform gold when industrial demand is high.',
    risks: 'More volatile than gold. Industrial demand can drop during recessions. Less liquid than gold as a financial asset.',
  },
  NG: {
    what: 'Natural gas is a fossil fuel used for heating homes, generating electricity, and as an industrial feedstock. The price affects home heating bills and electricity costs across the country.',
    why: 'Considered a "transition fuel" — cleaner than coal but still fossil fuel. Can spike in price during cold winters or supply disruptions.',
    risks: 'Extremely volatile — more so than oil. Heavily weather-dependent. Long-term demand uncertain as renewables expand.',
  },
  COPPER: {
    what: 'Copper is an industrial metal used in everything — electrical wiring, plumbing, electronics, EVs, and construction. It\'s so tied to economic activity that traders nicknamed it "Dr. Copper."',
    why: 'Rising copper prices signal a growing economy (more building, manufacturing, and infrastructure). Falling copper can be an early warning that the economy is slowing down. It\'s also a key input for electric vehicles and renewable energy.',
    risks: 'Heavily influenced by Chinese demand (China uses ~50% of global copper). A slowdown in China hits copper hard. Prices can swing on trade policy, mining disruptions, and global growth expectations.',
  },
  WHEAT: {
    what: 'Wheat is one of the world\'s most important food crops — used in bread, pasta, cereals, and livestock feed. The US is one of the world\'s largest wheat exporters.',
    why: 'Wheat prices are a direct input to food costs globally. When wheat prices spike, grocery prices follow. It\'s also a gauge of global food supply stress — wars, droughts, and supply chain disruptions all show up in the wheat price.',
    risks: 'Heavily weather-dependent — droughts and floods can wipe out supply overnight. Geopolitical events (like the Russia-Ukraine war, which disrupted a huge share of global wheat) can cause massive price spikes.',
  },
  '10Y': {
    what: 'The 10-Year Treasury Yield is the interest rate the US government pays on 10-year bonds. It\'s the single most important interest rate in the world — it\'s the benchmark for mortgage rates, car loans, corporate debt, and stock valuations.',
    why: 'When the 10Y yield rises, mortgages get more expensive, borrowing costs go up for companies, and stocks often fall. When it drops, borrowing gets cheaper and markets tend to rally. Watching the 10Y is how investors read the pulse of the economy.',
    risks: 'A rapidly rising yield can trigger market sell-offs. A falling yield can signal economic slowdown or recession fears. It\'s a signal, not an investment itself.',
  },
  '2Y': {
    what: 'The 2-Year Treasury is a short-term US government bond. It\'s closely watched because it reflects where the market expects the Federal Reserve to set interest rates in the near future.',
    why: 'Very safe, short time commitment, and currently pays a competitive yield. Widely used by institutions to park cash safely.',
    risks: 'Must reinvest when it matures, and rates may be lower then. Very low returns compared to stocks over time.',
  },
  '30Y': {
    what: 'The 30-Year Treasury is a long-term US government bond. The US government borrows your money for 30 years at a fixed rate.',
    why: 'Highest guaranteed yield among US government bonds. Attractive during periods when investors expect rates to fall.',
    risks: 'Long time horizon increases interest rate risk significantly. A 1% rise in rates can drop a 30-year bond\'s value by ~15-20%. Inflation over 30 years is a real concern.',
  },
  AAPL: {
    what: 'Apple is the most valuable company in the world by market cap. It makes the iPhone, Mac, iPad, Apple Watch, and earns massive revenue from its App Store, iCloud, and Apple Pay services.',
    why: 'Massive cash reserves, extremely loyal customer base, high profit margins, consistent dividend payments, and a growing services business.',
    risks: 'Heavily dependent on iPhone sales. Competition from Samsung and Chinese manufacturers. Regulatory scrutiny on App Store practices.',
  },
  MSFT: {
    what: 'Microsoft makes Windows, Office, Xbox, Azure (cloud computing), LinkedIn, and GitHub. It\'s one of the most diversified tech giants and a leader in cloud infrastructure.',
    why: 'Diversified revenue streams, dominant cloud business (Azure), strong AI investments (major OpenAI investor), pays a growing dividend.',
    risks: 'High valuation. Competition from Amazon AWS and Google Cloud. Antitrust scrutiny.',
  },
  TSLA: {
    what: 'Tesla designs and sells electric vehicles (EVs) and energy products. It\'s also developing autonomous driving software and AI. Often seen as part car company, part tech company.',
    why: 'Market leader in EVs, strong brand, unique vertically integrated supply chain, potential upside from full self-driving and energy storage.',
    risks: 'Extremely high valuation relative to earnings. CEO-related news risk. Intense competition from legacy automakers entering EV space. Regulatory risks.',
  },
  NVDA: {
    what: 'Nvidia designs graphics processing units (GPUs) — chips originally built for gaming that turned out to be essential for AI training. It now dominates the AI chip market.',
    why: 'Dominant market position in AI infrastructure. The go-to chip for training large AI models. Revenue and earnings have grown dramatically.',
    risks: 'Extremely high valuation. Depends heavily on continued AI investment. Export restrictions to China hurt revenue. Cyclical semiconductor industry.',
  },
  META: {
    what: 'Meta owns Facebook, Instagram, and WhatsApp — three of the world\'s largest social media platforms reaching over 3 billion people daily. It also invests heavily in VR/AR (Quest headsets).',
    why: 'Massive ad revenue, billions of daily users, significant AI investments, share buybacks, and recently started paying a dividend.',
    risks: 'Ad revenue can drop sharply in recessions. Privacy regulations could limit data use. Heavy spending on metaverse with uncertain returns.',
  },
  AMZN: {
    what: 'Amazon is the world\'s largest online retailer AND a major cloud computing company through AWS (Amazon Web Services). AWS often contributes more profit than all of retail combined.',
    why: 'Dominant in both e-commerce and cloud infrastructure. AWS powers huge portions of the internet. Prime membership creates loyal, recurring customers.',
    risks: 'Thin retail margins. Massive logistics costs. Competition from Walmart, Microsoft, and Google. Regulatory scrutiny in multiple countries.',
  },
  GOOGL: {
    what: 'Alphabet owns Google — the world\'s dominant search engine — as well as YouTube, Google Maps, Google Cloud, and Android. Google Search alone drives the majority of revenue through advertising.',
    why: 'Unmatched dominance in search and online advertising. YouTube is the world\'s second-largest search engine. Growing cloud business.',
    risks: 'Antitrust cases in US and EU could force structural changes. AI chatbots could challenge Google Search dominance. Ad revenue drops in recessions.',
  },
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtPrice = (p, type) => {
  if (p == null) return '—';
  if (type === 'rate') return p.toFixed(2) + '%';
  if (p > 10000) return '$' + p.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (p > 100)   return '$' + p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (p > 1)     return '$' + p.toFixed(2);
  return '$' + p.toFixed(4);
};

const fmtChange = (c) => {
  if (c == null) return { text: '—', up: null };
  const n = parseFloat(c);
  return { text: (n >= 0 ? '+' : '') + n.toFixed(2) + '%', up: n >= 0 };
};

// ── Ticker Card with expandable explainer ────────────────────────────────────
function TickerCard({ item, locked, pro }) {
  const [open, setOpen] = useState(false);
  const { text, up } = fmtChange(item.changePercent);
  const color  = up === true ? '#22c55e' : up === false ? '#ef4444' : '#64748b';
  const bg     = up === true ? 'rgba(34,197,94,0.07)' : up === false ? 'rgba(239,68,68,0.07)' : 'rgba(255,255,255,0.02)';
  const border = up === true ? 'rgba(34,197,94,0.2)'  : up === false ? 'rgba(239,68,68,0.2)'  : '#1e293b';
  const explainer = EXPLAINERS[item.symbol];

  if (locked) {
    return (
      <div style={{ ...styles.card, opacity: 0.45, filter: 'blur(1px)', pointerEvents: 'none', background: '#1e293b', borderColor: '#334155' }}>
        <div style={styles.cardTop}>
          <div><div style={styles.sym}>••••</div><div style={styles.name}>Pro Only</div></div>
          <div style={styles.changePill}>🔒</div>
        </div>
        <div style={styles.price}>$—</div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.card, background: bg, borderColor: open ? 'rgba(56,189,248,0.35)' : border }}>
      {/* Top row */}
      <div style={styles.cardTop}>
        <div>
          <div style={styles.sym}>{item.symbol}</div>
          <div style={styles.name}>{item.name}</div>
        </div>
        <div style={{ ...styles.changePill, color, background: up !== null ? (up ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)') : '#1e293b' }}>
          {up === true ? '▲' : up === false ? '▼' : ''} {text}
        </div>
      </div>

      {/* Price */}
      <div style={styles.price}>{fmtPrice(item.price, item.type)}</div>
      <div style={styles.typeTag}>{item.type}</div>

      {/* Explain button */}
      {explainer && (
        <button
          style={{ ...styles.explainBtn, ...(open ? styles.explainBtnOpen : {}), ...(!pro ? { opacity: 0.7 } : {}) }}
          onClick={() => setOpen(o => !o)}
        >
          {!pro ? '🔒 Explain ▼' : open ? 'Hide ▲' : 'Explain ▼'}
        </button>
      )}

      {/* Expanded explainer */}
      {open && explainer && (
        pro ? (
          <div style={styles.explainer}>
            <div style={styles.explainSection}>
              <div style={styles.explainLabel}>📖 What it is</div>
              <p style={styles.explainText}>{explainer.what}</p>
            </div>
            <div style={styles.explainSection}>
              <div style={styles.explainLabel}>✅ Why some people invest</div>
              <p style={styles.explainText}>{explainer.why}</p>
            </div>
            <div style={styles.explainSection}>
              <div style={styles.explainLabel}>⚠️ Key risks</div>
              <p style={styles.explainText}>{explainer.risks}</p>
            </div>
            <p style={styles.explainDisclaimer}>This is factual information only — not a recommendation to buy or sell.</p>
          </div>
        ) : (
          <div style={styles.explainer}>
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{ fontSize: '22px', marginBottom: '8px' }}>🔒</div>
              <div style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '14px', marginBottom: '6px' }}>Pro Feature</div>
              <p style={{ color: '#64748b', fontSize: '13px', lineHeight: 1.6, marginBottom: '14px' }}>
                Get plain-English explanations for every ticker — what it is, why people invest, and the real risks.
              </p>
              <Link to="/signup?plan=pro" style={{ background: 'linear-gradient(135deg, #0ea5e9, #6366f1)', color: '#fff', borderRadius: '8px', padding: '8px 18px', fontWeight: '700', fontSize: '13px', textDecoration: 'none' }}>
                Upgrade to Pro — $12/mo →
              </Link>
            </div>
          </div>
        )
      )}
    </div>
  );
}

// ── Mini sparkline chart ─────────────────────────────────────────────────────
function MiniChart({ data, color = '#38bdf8' }) {
  if (!data || data.length < 2) return null;
  const vals = data.map(d => d.rate);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const W = 300, H = 60;
  const points = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * W;
    const y = H - ((v - min) / range) * H * 0.85 - 4;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible' }}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
      <circle cx={points.split(' ').pop().split(',')[0]} cy={points.split(' ').pop().split(',')[1]} r="3" fill={color} />
    </svg>
  );
}

// ── Rates Panel ──────────────────────────────────────────────────────────────
function RatesPanel({ rates, pro }) {
  if (!rates) return <div style={styles.loading}>Loading rate data...</div>;
  const items = [
    { label: 'Fed Funds Rate', value: rates.current != null ? rates.current.toFixed(2) + '%' : '—', desc: 'Set by the Federal Reserve. Controls the cost of borrowing across the entire economy — mortgages, car loans, credit cards, and business loans all move with this rate.' },
    { label: '10-Year Treasury', value: rates.treasury10y != null ? rates.treasury10y.toFixed(2) + '%' : '—', desc: 'The benchmark rate for 30-year mortgages and long-term corporate debt. When this rises, home loans get more expensive. It reflects the market\'s long-term growth and inflation expectations.' },
    { label: '30-Yr Mortgage', value: rates.mortgage30y != null ? rates.mortgage30y.toFixed(2) + '%' : '—', desc: 'The average US 30-year fixed mortgage rate. This directly affects how much house you can afford. Every 1% rise adds roughly $150–$200/month to a $300,000 mortgage.' },
  ];
  return (
    <div>
      <div style={styles.rateCards}>
        {items.map((item, i) => (
          <div key={i} style={styles.rateCard}>
            <div style={styles.rateLabel}>{item.label}</div>
            <div style={styles.rateValue}>{item.value}</div>
            <p style={styles.rateDesc}>{item.desc}</p>
          </div>
        ))}
      </div>
      {rates.nextMeeting && (
        <div style={styles.fomcBanner}>
          <span style={styles.fomcIcon}>📅</span>
          <div>
            <strong>Next Fed Meeting:</strong> {rates.nextMeeting.label}
            <span style={styles.fomcNote}> — The Federal Open Market Committee meets 8 times per year to decide whether to raise, cut, or hold interest rates.</span>
          </div>
        </div>
      )}
      <div style={styles.chartCard}>
        <div style={styles.chartHeader}>
          <h3 style={styles.chartTitle}>Fed Funds Rate — 5 Year History</h3>
          {!pro && <Link to="/signup?plan=pro" style={styles.proTag}>🔒 Pro: Full history</Link>}
        </div>
        {pro ? (
          <>
            <MiniChart data={rates.history} color="#38bdf8" />
            <div style={styles.chartContext}>
              <p style={styles.contextText}>📌 <strong>2022–2023:</strong> The Fed raised rates 11 times (from ~0% to 5.25–5.5%) to fight post-COVID inflation that peaked at 9.1% in June 2022 — the fastest rate hike cycle in 40 years.</p>
              <p style={styles.contextText}>📌 <strong>2024–2025:</strong> Inflation cooled to ~2.5–3%, so the Fed began cutting rates. Mortgage and borrowing costs are gradually easing.</p>
              <p style={styles.contextText}>📌 <strong>What it means for you:</strong> Lower rates reduce the cost of mortgages, car loans, and credit card interest. Higher rates mean the opposite — but savings accounts and CDs pay more.</p>
            </div>
          </>
        ) : (
          <div style={styles.chartBlurred}>
            <MiniChart data={rates.history?.slice(-12)} color="#38bdf8" />
            <div style={styles.blurOverlay}>
              <Link to="/signup?plan=pro" style={styles.unlockBtn}>🔓 Unlock Full History with Pro</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, items, pro }) {
  if (!items.length) return null;
  return (
    <div style={styles.section}>
      <h2 style={styles.sectionTitle}>{title}</h2>
      <div style={styles.grid}>
        {items.map((item, i) => <TickerCard key={i} item={item} locked={false} pro={pro} />)}
      </div>
    </div>
  );
}

function UpgradeBanner() {
  return (
    <div style={styles.upgradeBanner}>
      <div>
        <strong style={{ color: '#f1f5f9' }}>🔒 Unlock Pro Markets</strong>
        <p style={styles.upgradeDesc}>Unlock Fear & Greed Index, Sector Performance, BTC Dominance, Global Currencies + full interest rate history.</p>
      </div>
      <Link to="/signup?plan=pro" style={styles.upgradeBtn}>Upgrade to Pro — $12/mo</Link>
    </div>
  );
}

function makeStyles(isMobile = false) {
  return {
    page: { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: isMobile ? '48px 16px' : '60px 40px', maxWidth: '1100px', margin: '0 auto', position: 'relative' },
    glow: { position: 'absolute', top: '-60px', left: '50%', transform: 'translateX(-50%)', width: '700px', height: '500px', background: 'radial-gradient(ellipse at center, rgba(56,189,248,0.08) 0%, transparent 60%)', pointerEvents: 'none', zIndex: 0 },

    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px', position: 'relative', zIndex: 1, flexWrap: 'wrap', gap: '12px' },
    title: { fontSize: '40px', fontWeight: '800', marginBottom: '8px', letterSpacing: '-0.5px' },
    subtitle: { color: '#64748b', fontSize: '15px', margin: 0 },
    refreshArea: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' },
    updatedAt: { color: '#475569', fontSize: '12px' },
    refreshBtn: { background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.2)', color: '#38bdf8', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' },
    refreshBtnSpin: { opacity: 0.6 },

    tabs: { display: 'flex', gap: '6px', marginBottom: '32px', flexWrap: 'wrap', position: 'relative', zIndex: 1 },
    tab: { background: 'transparent', border: '1px solid #334155', color: '#64748b', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit' },
    tabActive: { background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.3)', color: '#f1f5f9' },
    lockIcon: { fontSize: '11px' },

    loading: { textAlign: 'center', color: '#475569', padding: '80px 0', position: 'relative', zIndex: 1 },
    section: { marginBottom: '40px', position: 'relative', zIndex: 1 },
    sectionTitle: { fontSize: '18px', fontWeight: '700', marginBottom: '16px', color: '#f1f5f9' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))', gap: '14px' },

    // Ticker cards
    card: { borderRadius: '14px', padding: '18px', border: '1px solid', transition: 'border-color 0.2s', position: 'relative', zIndex: 1 },
    cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' },
    sym: { fontSize: '11px', fontWeight: '800', color: '#94a3b8', letterSpacing: '0.5px', marginBottom: '2px' },
    name: { fontSize: '13px', fontWeight: '600', color: '#f1f5f9' },
    changePill: { padding: '3px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '700' },
    price: { fontSize: '22px', fontWeight: '800', color: '#f8fafc', marginBottom: '4px' },
    typeTag: { fontSize: '10px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' },

    explainBtn: { width: '100%', background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.15)', color: '#64748b', padding: '7px 0', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.3px' },
    explainBtnOpen: { background: 'rgba(56,189,248,0.12)', color: '#38bdf8', borderColor: 'rgba(56,189,248,0.3)' },

    explainer: { marginTop: '14px', borderTop: '1px solid rgba(56,189,248,0.15)', paddingTop: '14px' },
    explainSection: { marginBottom: '12px' },
    explainLabel: { fontSize: '11px', fontWeight: '700', color: '#38bdf8', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: '4px' },
    explainText: { color: '#94a3b8', fontSize: '13px', lineHeight: 1.65, margin: 0 },
    explainDisclaimer: { color: '#334155', fontSize: '11px', margin: '10px 0 0', fontStyle: 'italic' },

    // Rates
    rateCards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '20px' },
    rateCard: { background: '#1e293b', borderRadius: '14px', padding: '20px', border: '1px solid #334155' },
    rateLabel: { fontSize: '11px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
    rateValue: { fontSize: '32px', fontWeight: '800', color: '#38bdf8', marginBottom: '8px' },
    rateDesc: { color: '#64748b', fontSize: '12px', lineHeight: 1.55, margin: 0 },

    fomcBanner: { display: 'flex', alignItems: 'flex-start', gap: '12px', background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.15)', borderRadius: '12px', padding: '14px 18px', marginBottom: '20px', fontSize: '14px', color: '#94a3b8' },
    fomcIcon: { fontSize: '18px', flexShrink: 0 },
    fomcNote: { color: '#64748b' },

    chartCard: { background: '#1e293b', borderRadius: '14px', padding: '24px', border: '1px solid #334155', marginBottom: '20px' },
    chartHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    chartTitle: { fontSize: '16px', fontWeight: '700', color: '#f1f5f9', margin: 0 },
    proTag: { background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.2)', color: '#38bdf8', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', textDecoration: 'none' },
    chartContext: { marginTop: '20px', borderTop: '1px solid #334155', paddingTop: '16px' },
    contextText: { color: '#64748b', fontSize: '13px', lineHeight: 1.6, marginBottom: '10px' },
    chartBlurred: { position: 'relative' },
    blurOverlay: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', background: 'rgba(15,23,42,0.5)', borderRadius: '8px' },
    unlockBtn: { background: '#38bdf8', color: '#0f172a', padding: '10px 20px', borderRadius: '8px', fontWeight: '700', fontSize: '14px', textDecoration: 'none' },

    upgradeBanner: { background: 'linear-gradient(135deg, rgba(56,189,248,0.08) 0%, rgba(99,102,241,0.06) 100%)', border: '1px solid rgba(56,189,248,0.2)', borderRadius: '16px', padding: '24px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', position: 'relative', zIndex: 1, marginTop: '8px' },
    upgradeDesc: { color: '#64748b', fontSize: '14px', margin: '4px 0 0' },
    upgradeBtn: { background: '#38bdf8', color: '#0f172a', padding: '12px 24px', borderRadius: '10px', fontWeight: '700', fontSize: '15px', textDecoration: 'none', whiteSpace: 'nowrap' },

    howToBanner: { background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '16px', padding: '24px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', position: 'relative', zIndex: 1, marginTop: '40px', marginBottom: '20px' },
    howToDesc: { color: '#64748b', fontSize: '14px', margin: '4px 0 0' },
    howToBtn: { background: 'transparent', border: '2px solid rgba(99,102,241,0.4)', color: '#a5b4fc', padding: '12px 24px', borderRadius: '10px', fontWeight: '700', fontSize: '15px', textDecoration: 'none', whiteSpace: 'nowrap' },

    disclaimer: { color: '#334155', fontSize: '12px', textAlign: 'center', marginTop: '20px', lineHeight: 1.6, position: 'relative', zIndex: 1 },
  };
}

const styles = makeStyles();

// ── Main ─────────────────────────────────────────────────────────────────────
      {/* Email capture */}
      <div style={{ marginTop: '48px', marginBottom: '24px' }}>
        <EmailCapture variant="card" source="market" title="Get smarter about money — free." subtitle="The KnowFi Daily Brief: plain English market news every morning." />
      </div>

export default function Market() {
  const isMobile = useIsMobile();
  const styles = makeStyles(isMobile);

  const [data, setData]     = useState([]);
  const [rates, setRates]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [updated, setUpdated] = useState(null);
  const [countdown, setCountdown] = useState(60);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab]       = useState('overview');
  const pro  = isPro();
  const tier = pro ? 'pro' : 'free';
  const countdownRef = useRef(null);

  const fetchAll = async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const [mRes, rRes] = await Promise.all([
        fetch(`${API}/api/market-data?tier=${tier}`).then(r => r.json()),
        fetch(`${API}/api/rates`).then(r => r.json()),
      ]);
      setData(mRes.data || []);
      setUpdated(new Date().toLocaleTimeString());
      setRates(rRes);
      setLoading(false);
      setCountdown(60);
    } catch {
      setLoading(false);
    }
    if (manual) setRefreshing(false);
  };

  // Auto-refresh every 60 seconds with countdown
  useEffect(() => {
    fetchAll();
    const interval = setInterval(() => fetchAll(), 60000);
    countdownRef.current = setInterval(() => {
      setCountdown(c => (c <= 1 ? 60 : c - 1));
    }, 1000);
    return () => {
      clearInterval(interval);
      clearInterval(countdownRef.current);
    };
  }, []); // eslint-disable-line

  const crypto      = data.filter(d => d.type === 'crypto');
  const indices     = data.filter(d => d.type === 'index');
  const commodities = data.filter(d => d.type === 'commodity');

  const TABS = ['overview', 'crypto', 'commodities', 'rates'];

  return (
    <div style={styles.page}>
      <SEO
        title="Live Market Data — Stocks, Crypto, Rates & Indices"
        description="Live prices for Bitcoin, Ethereum, S&P 500, Nasdaq, gold, and more. Plus current interest rates and mortgage rates — all in one place."
        path="/market"
        keywords="live stock prices, Bitcoin price, S&P 500 today, live market data, crypto prices, interest rates"
      />
      <div style={styles.glow} />

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Live Markets</h1>
          <p style={styles.subtitle}>
            Real-time prices — click <strong style={{ color: '#38bdf8' }}>Explain ▼</strong> on any card to understand what it is and how it works.
          </p>
        </div>
        <div style={styles.refreshArea}>
          {updated && <span style={styles.updatedAt}>Updated {updated} · refreshes in {countdown}s</span>}
          <button onClick={() => fetchAll(true)} style={{ ...styles.refreshBtn, ...(refreshing ? styles.refreshBtnSpin : {}) }}>
            {refreshing ? '⏳' : '↻'} Refresh
          </button>
        </div>
      </div>

      <ProMarketIntel pro={pro} />

      {/* Tabs */}
      <div style={styles.tabs}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ ...styles.tab, ...(tab === t ? styles.tabActive : {}) }}>
            {t.charAt(0).toUpperCase() + t.slice(1)}

          </button>
        ))}
      </div>

      {loading ? (
        <div style={styles.loading}>Loading market data...</div>
      ) : (
        <>
          {tab === 'overview' && (
            <div>
              <Section title="📈 Indices & Rates" items={indices} pro={pro} />
              <Section title="₿ Crypto" items={crypto} pro={pro} />
              <Section title="🛢️ Commodities, Metals & Food" items={commodities} pro={pro} />
              {!pro && <UpgradeBanner />}
            </div>
          )}
          {tab === 'crypto'      && <Section title="₿ Cryptocurrency" items={crypto} pro={pro} />}
          {tab === 'commodities' && <Section title="🛢️ Commodities, Metals & Food" items={commodities} pro={pro} />}
          {tab === 'rates'       && (
            <div>
              <h2 style={styles.sectionTitle}>📊 Interest Rates & Federal Reserve</h2>
              <RatesPanel rates={rates} pro={pro} />
            </div>
          )}
        </>
      )}

      <div style={styles.howToBanner}>
        <div>
          <strong style={{ color: '#f1f5f9' }}>🎓 New to investing?</strong>
          <p style={styles.howToDesc}>Not sure how to actually buy any of this? Our step-by-step guides walk you through opening a brokerage account, Roth IRA, 401k, and more.</p>
        </div>
        <Link to="/howto" style={styles.howToBtn}>How-To Guides →</Link>
      </div>

      <p style={styles.disclaimer}>
        Data sourced from CoinGecko, Yahoo Finance, and FRED (Federal Reserve Bank of St. Louis).
        May be delayed up to 15 minutes. For educational purposes only — not financial advice.
      </p>
    </div>
  );
}

